import {GROUP_KEYS, useRecommendedInfluencersQuery} from "@/api/groupApi";
import EditIcon from "@/assets/colorable/edit.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import EmptyState from "@/components/Campaign/management/components/EmptyState";
import Button from "@/components/Common/Button";
import FullPageLoader from "@/components/Common/FullPageLoader";
import NotFound from "@/components/Common/NotFound";
import PermissionDenied from "@/components/Common/PermissionDenied";
import {Tooltip} from "@/components/Common/Tooltip";
import Loader from "@/components/Common/kpi/Loader";
import AmbassadorGroup from "@/components/Groups/AmbassadorGroup";
import CreateCampaignFromGroupButton from "@/components/Groups/CreateCampaignFromGroupButton";
import DeleteGroupModal from "@/components/Groups/DeleteGroupModal";
import EditGroupModal from "@/components/Groups/EditGroupModal";
import GroupCsvDownloadLink from "@/components/Groups/GroupCsvDownloadLink";
import GroupFiltersBar from "@/components/Groups/GroupFiltersBar";
import NoFilterMatchesState from "@/components/Groups/NoFilterMatchesState";
import InfluencerGrid from "@/components/Influencer/InfluencerGrid";
import InfluencerCard from "@/components/Influencer/influencerNetwork/components/InfluencerCard";
import type {GroupInfluencerListDetailSerializer, InfluencerProfileDetailSerializer} from "@/generated/DjangoTypes";
import {useGroupPage} from "@/hooks/useGroupPage";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {useGroupStore} from "@/store/group";
import useGroupInfluencerFilterStore from "@/store/groupInfluencerFilter";
import {InfluencerSidebarStoreProvider} from "@/store/influencerSidebarContext";
import {groupDeleteAtom, influencerViewModeAtom} from "@/store/jotaiAtoms";
import {groupName} from "@/utils/groupUtils";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {useAtom, useAtomValue} from "jotai";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle, useUnmount} from "react-use";

export const Route = createFileRoute("/_authenticated/group/$groupId/")({
  component: Group,
  params: {
    parse: raw => ({groupId: +raw.groupId}),
    stringify: params => ({groupId: params.groupId?.toString()}),
  },
});

function Group() {
  const {t} = useTranslation(["common", "referral", "influencer_list"]);
  const navigate = Route.useNavigate();
  const router = useRouter();
  const {groupId} = Route.useParams();
  const setGroupValue = useGroupStore(state => state.setValue);
  const [groupToDelete, setGroupToDelete] = useAtom(groupDeleteAtom);
  const [isEditGroupModalVisible, setIsEditGroupModalVisible] = useState(false);
  const viewMode = useAtomValue(influencerViewModeAtom);

  const {
    activeFilters,
    group,
    groupError,
    influencersQuery,
    influencersQueryRef,
    isFetchingSimilar,
    isGroupPending,
    renderActions,
    resetAllFilters,
    showVibeActions,
  } = useGroupPage({groupId: Number(groupId)});
  const resetFilters = useGroupInfluencerFilterStore(state => state.reset);

  const hasActiveFilters = activeFilters.length > 0;

  useTitle(
    !group ? t("common:groups") : group.name === "brand ambassador" ? t("common:brandAmbassador") : groupName(group),
  );

  const influencerCount = influencersQuery.data?.count ?? 0;
  const recommendedInfluencersQuery = useRecommendedInfluencersQuery({
    agencyId: !!group?.agency ? +group.agency : undefined,
    companyId: !!group?.company ? +group?.company : undefined,
    queryOptions: {
      refetchOnMount: true,
      enabled:
        !!groupId &&
        !!influencersQuery.data &&
        (!!group?.agency || !!group?.company) &&
        !influencerCount &&
        !hasActiveFilters &&
        group?.name !== "brand ambassador",
    },
  });
  const {ref: recommendedInfluencersRef} = useInViewWithQuery(recommendedInfluencersQuery);
  const similarInfluencersQuery = useGenericInfiniteQuery<GroupInfluencerListDetailSerializer>({
    queryKey: GROUP_KEYS.qGroupSimilarInfluencers(Number(groupId)),
    enabled:
      !!groupId &&
      !!influencersQuery.data &&
      !!influencerCount &&
      !hasActiveFilters &&
      group?.name !== "brand ambassador",
    refetchOnMount: true,
  });
  const {ref: similarInfluencersRef} = useInViewWithQuery(similarInfluencersQuery);

  const flattenedSimilarInfluencers =
    (!!influencerCount ? similarInfluencersQuery.data : recommendedInfluencersQuery.data)?.filter(
      influencer => !!influencer,
    ) ?? [];

  useUnmount(resetFilters);

  if (isGroupPending) {
    return <FullPageLoader />;
  }

  if (groupError) {
    if (groupError.response?.status === 404) {
      return <NotFound message={t("groupNotExists")} page={t("dashboard")} redirect="/" />;
    }

    return <PermissionDenied />;
  }

  if (!group) {
    return null;
  }

  if (group.name === "brand ambassador") {
    return <AmbassadorGroup />;
  }

  const showSimilarSection = !group.smart && !hasActiveFilters;
  const campaignCompanyId = group.company ?? null;

  return (
    <>
      {isEditGroupModalVisible && <EditGroupModal group={group} onClose={() => setIsEditGroupModalVisible(false)} />}
      {!!groupToDelete && <DeleteGroupModal onClose={() => setGroupToDelete(null)} />}
      <div className="bg-neutral-grey-lightest min-h-screen">
        <div className="max-w-wide container mx-auto mt-8 mb-20 flex flex-col justify-center gap-7 px-4 pb-20 sm:px-8">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-4">
            <div className="flex min-w-0 items-center gap-5">
              {window.history.length > 2 && (
                <Button
                  buttonStyle="White"
                  className="size-8 shrink-0"
                  onClick={() => {
                    router.history.back();
                  }}>
                  <RightArrowIcon className="text-primary-dark-solid size-3 -scale-x-100" />
                </Button>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-good-grey-900 text-2.5xl leading-5 font-semibold">{groupName(group)}</span>
                  <span className="text-primary-dark-solid bg-neutral-grey-light-dark flex min-h-9 min-w-9 items-center justify-center rounded-full px-2 py-1 text-xl font-semibold">
                    {influencersQuery.isLoading ? (
                      <Loader styles={{loaderClassName: "size-5 [&_path:last-child]:fill-primary-dark-solid"}} />
                    ) : (
                      influencerCount
                    )}
                  </span>
                </div>
                {!group.smart && (
                  <Can I="update" a="Group">
                    <Button
                      buttonStyle="White"
                      onClick={() => {
                        setGroupValue("name", group.name);
                        setGroupValue("description", group.description ?? "");
                        setIsEditGroupModalVisible(true);
                      }}
                      className="border-primary-light-solid size-9 shrink-0 rounded-full border">
                      <EditIcon className="text-primary-dark-solid size-4 shrink-0" />
                    </Button>
                  </Can>
                )}
                {!group.smart && (
                  <Can I="update" a="Group">
                    <Button
                      data-tip
                      data-tooltip-id={`group-${group.id}-add-from-network`}
                      buttonStyle="White"
                      onClick={() => navigate({to: "/influencers-list"})}
                      className="border-primary-light-solid size-9 shrink-0 rounded-full border">
                      <PlusIcon className="text-primary-dark-solid size-4 shrink-0" />
                    </Button>
                    <Tooltip id={`group-${group.id}-add-from-network`} place="bottom" variant="dark" className="z-100">
                      {t("addMoreInfluencersFromNetwork")}
                    </Tooltip>
                  </Can>
                )}
                <GroupCsvDownloadLink group={group} />
              </div>
            </div>
            {!!campaignCompanyId && (
              <CreateCampaignFromGroupButton
                companyId={campaignCompanyId}
                cutoffValue={influencersQuery.data?.cutoffValue}
                groupId={group.id}
                influencerCount={influencersQuery.data?.count}
              />
            )}
          </div>
          {!group.smart && !!group.description && (
            <p className="text-neutral-black mb-0 max-w-130 self-center text-center">{group.description}</p>
          )}
          <GroupFiltersBar
            fetchedInfluencerCount={influencersQuery.data?.count}
            groupId={groupId}
            vibeSearchPlaceholder={t("influencer_list:vibe_search")}
          />
          {influencerCount === 0 && !influencersQuery.isLoading && !isFetchingSimilar ? (
            hasActiveFilters ? (
              <NoFilterMatchesState
                onClearFilters={resetAllFilters}
                renderActions={renderActions}
                showVibeActions={showVibeActions}
              />
            ) : (
              <div className="bg-primary-light-solid flex w-full max-w-150 flex-col gap-10 self-center rounded-md p-5">
                <div className="max-w-96 self-center bg-linear-(--button-gradient) bg-clip-text text-center text-3xl font-semibold text-transparent">
                  {t("noInfluencersInGroup")}
                </div>
                <Button
                  buttonStyle="White"
                  className="rounded-full font-semibold enabled:hover:bg-white"
                  onClick={() => navigate({to: "/influencers-list"})}>
                  {t("goToInfluencerNetwork")}
                </Button>
              </div>
            )
          ) : (
            <div
              className={clsxm(
                "flex w-full flex-col gap-4 rounded-xl bg-white p-5",
                viewMode === "grid" && "contents",
              )}>
              <InfluencerSidebarStoreProvider>
                <InfluencerGrid
                  actionsSlot={showVibeActions ? renderActions() : undefined}
                  influencerListItemClassName="first:border-t-0"
                  currentGroup={group}
                  data={influencersQuery.data?.results}
                  isFetching={influencersQuery.isFetching || isFetchingSimilar}
                  isInCurrentGroup
                  isLoading={influencersQuery.isLoading}
                  queryRef={influencersQueryRef}
                  totalCount={isFetchingSimilar ? undefined : influencersQuery.data?.count}
                />
              </InfluencerSidebarStoreProvider>
            </div>
          )}
          {showSimilarSection && (
            <div className="my-2 flex items-center justify-center gap-2">
              <div className="h-px flex-1 bg-gray-100" />
              <div className="flex items-center justify-center gap-2 rounded-full bg-gray-100 px-3 py-1">
                <span className="text-neutral-grey-dark text-sm font-semibold">
                  {t(!influencerCount ? "influencersSimilarToProfile" : "influencersSimilarToGroup")}
                </span>
              </div>
              <div className="h-px flex-1 bg-gray-100" />
            </div>
          )}
          {!showSimilarSection ? null : similarInfluencersQuery.isLoading || recommendedInfluencersQuery.isLoading ? (
            <Loader />
          ) : !flattenedSimilarInfluencers.length ? (
            <EmptyState title={t("noRecommendedInfluencers")} className="my-0" />
          ) : (
            <>
              <div className="grid w-full grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                {flattenedSimilarInfluencers.map(influencer => (
                  <InfluencerCard
                    key={influencer.uuid}
                    influencer={influencer as unknown as InfluencerProfileDetailSerializer}
                    currentGroup={group}
                    campaignType={
                      influencer.user.is_verified ? "instagram" : influencer.user.tiktok_verified ? "tiktok" : "ugc"
                    }
                  />
                ))}
              </div>
              {!similarInfluencersQuery.isFetching && similarInfluencersQuery.hasNextPage && (
                <div ref={similarInfluencersRef} />
              )}
              {similarInfluencersQuery.isFetching && (
                <Loader
                  styles={{
                    containerClassName: "items-center justify-center",
                  }}
                />
              )}
              {!recommendedInfluencersQuery.isFetching && recommendedInfluencersQuery.hasNextPage && (
                <div ref={recommendedInfluencersRef} />
              )}
              {recommendedInfluencersQuery.isFetching && (
                <Loader
                  styles={{
                    containerClassName: "items-center justify-center",
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
